import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, FileCheck2 } from 'lucide-react';
import { api, hlaskaZChyby } from '../api/client.js';
import type { Podpis } from '../api/types.js';
import { useAuth } from '../auth/AuthContext.js';
import { formatKc } from '../lib/format.js';
import { PodpisNahled } from '../components/PodpisNahled.js';
import { PodpisProtistranyDialog } from '../components/PodpisProtistranyDialog.js';
import { PodpisVstup } from '../components/PodpisVstup.js';
import { Badge, Btn, Chyba, Dialog, Nacitani, th, td } from '../components/ui.js';

interface RadekProtokolu {
  soupisPolozkaId?: string;
  zmenovyListPolozkaId?: string;
  kod: string;
  popis: string;
  jednotka: string;
  celkem: string | null;
  drivezCerpanoProcent: string;
  procentoCerpani: string | null;
  castka: string | null;
}

interface ProtokolDetail {
  id: string;
  obdobi: string;
  stav: 'navrzeno' | 'odsouhlaseno';
  suma: string;
  smlouva: {
    id: string;
    cislo: string | null;
    typ: string;
    protistrana: string;
    zakazka: { cislo: string; nazev: string };
  };
  navrhl?: { jmeno: string } | null;
  odsouhlasil?: { jmeno: string } | null;
  datumOdsouhlaseni?: string | null;
  radkySoupis: RadekProtokolu[];
  radkyZl: RadekProtokolu[];
  podpisy?: Podpis[];
}

/**
 * Detail zjišťovacího protokolu — měsíční čerpání proti soupisu (a položkám ZL
 * z podepsaných dodatků): návrh % po položkách → odsouhlasení s podpisem
 * (i protistranou přes odkaz/QR) → PDF podklad k faktuře.
 */
export function ProtokolDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { me, maPravo } = useAuth();
  const queryClient = useQueryClient();
  const [cerpani, setCerpani] = useState<Record<string, string>>({});
  const [odsouhlasuji, setOdsouhlasuji] = useState(false);
  const [protistrana, setProtistrana] = useState(false);
  const [chyba, setChyba] = useState<string | null>(null);

  const { data: p, isLoading } = useQuery({
    queryKey: ['protokol', id],
    queryFn: () => api.get<ProtokolDetail>(`/api/protokoly/${id}`),
  });

  const klicRadku = (r: RadekProtokolu) => r.soupisPolozkaId ?? `zl:${r.zmenovyListPolozkaId}`;
  const hodnota = (r: RadekProtokolu) => cerpani[klicRadku(r)] ?? r.procentoCerpani ?? '';

  // Živý součet — levné i pro velké soupisy, počítá se přímo v renderu.
  let zivaSuma = 0;
  if (p) {
    for (const r of [...p.radkySoupis, ...p.radkyZl]) {
      const proc = Number(String(hodnota(r)).replace(',', '.')) || 0;
      zivaSuma += (Number(r.celkem ?? 0) * proc) / 100;
    }
  }

  const uloz = useMutation({
    mutationFn: () => {
      const polozky = [...(p?.radkySoupis ?? []), ...(p?.radkyZl ?? [])]
        .map((r) => ({ r, proc: String(hodnota(r)).trim() }))
        .filter(({ proc }) => proc !== '' && Number(proc.replace(',', '.')) > 0)
        .map(({ r, proc }) => ({
          ...(r.soupisPolozkaId
            ? { soupisPolozkaId: r.soupisPolozkaId }
            : { zmenovyListPolozkaId: r.zmenovyListPolozkaId }),
          procentoCerpani: proc,
        }));
      return api.patch<ProtokolDetail>(`/api/protokoly/${id}/polozky`, { polozky });
    },
    onSuccess: (novy) => {
      queryClient.setQueryData(['protokol', id], novy);
      setCerpani({});
      setChyba(null);
    },
    onError: (err) => setChyba(hlaskaZChyby(err)),
  });

  if (isLoading || !p) return <Nacitani text="Načítám protokol…" />;
  const editovatelny = p.stav === 'navrzeno' && maPravo('finance.write');

  return (
    <>
      {/* Masthead dokumentu: titul vlevo, STAV razítko (na mobilu nad titulkem). */}
      <header className="flex flex-col-reverse items-start gap-3 border-b border-border pb-4 sm:flex-row sm:justify-between sm:gap-4">
        <div className="min-w-0 flex-1">
          <div className="text-sm text-text-secondary">
            <Link to="/smlouvy" className="underline underline-offset-2">
              Smlouvy
            </Link>{' '}
            /{' '}
            <Link to={`/smlouvy/${p.smlouva.id}`} className="underline underline-offset-2">
              {p.smlouva.cislo ?? p.smlouva.protistrana}
            </Link>{' '}
            / protokol
          </div>
          <h1 className="text-xl font-bold lg:text-2xl">Zjišťovací protokol {p.obdobi}</h1>
          <p className="text-base text-text-secondary">
            {p.smlouva.zakazka.cislo} — {p.smlouva.zakazka.nazev} · {p.smlouva.protistrana}
          </p>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-2">
          <div className="rounded-lg bg-bg px-3 py-2 text-center">
            <p className="text-xs font-semibold tracking-wider text-text-secondary uppercase">
              Stav
            </p>
            <Badge stav={p.stav === 'odsouhlaseno' ? 'success' : 'warning'}>
              {p.stav === 'odsouhlaseno' ? 'odsouhlasen' : 'návrh'}
            </Badge>
          </div>
          <a
            href={`/api/protokoly/${p.id}/pdf`}
            download
            className="flex items-center gap-1.5 text-sm font-semibold text-primary underline underline-offset-2"
          >
            <Download size={14} strokeWidth={2} aria-hidden="true" /> PDF
          </a>
        </div>
      </header>

      {(p.podpisy ?? []).length > 0 && (
        <div className="flex flex-wrap gap-2">
          {(p.podpisy ?? []).map((pod) => (
            <PodpisNahled key={pod.id} podpis={pod} />
          ))}
        </div>
      )}

      {chyba && <Chyba>{chyba}</Chyba>}

      <TabulkaCerpani
        nadpis="Položky soupisu prací"
        radky={p.radkySoupis}
        editovatelny={editovatelny}
        hodnota={hodnota}
        onZmena={(r, v) => setCerpani((c) => ({ ...c, [klicRadku(r)]: v }))}
      />
      {p.radkyZl.length > 0 && (
        <TabulkaCerpani
          nadpis="Vícepráce z podepsaných dodatků"
          radky={p.radkyZl}
          editovatelny={editovatelny}
          hodnota={hodnota}
          onZmena={(r, v) => setCerpani((c) => ({ ...c, [klicRadku(r)]: v }))}
        />
      )}

      {/* Lišta: živá Σ + akce. */}
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-surface p-4">
        <p className="text-lg font-bold tabular-nums">
          Σ k fakturaci: {formatKc(String(Math.round(zivaSuma)))}
          <span className="ml-2 text-sm font-normal text-text-secondary">bez DPH</span>
        </p>
        {editovatelny && (
          <div className="flex flex-wrap gap-2">
            <Btn
              size="desktop"
              varianta="secondary"
              disabled={uloz.isPending}
              onClick={() => uloz.mutate()}
            >
              {uloz.isPending ? 'Ukládám…' : 'Uložit čerpání'}
            </Btn>
            <Btn size="desktop" varianta="secondary" onClick={() => setProtistrana(true)}>
              Odsouhlasení protistrany
            </Btn>
            <Btn size="desktop" onClick={() => setOdsouhlasuji(true)}>
              <FileCheck2 size={16} strokeWidth={2} aria-hidden="true" />
              Odsouhlasit a zamknout
            </Btn>
          </div>
        )}
      </div>

      {odsouhlasuji && (
        <OdsouhlasitDialog
          protokolId={p.id}
          obdobi={p.obdobi}
          jmeno={me?.jmeno ?? ''}
          onZavrit={() => setOdsouhlasuji(false)}
        />
      )}
      {protistrana && (
        <PodpisProtistranyDialog
          entita="ZjistovaciProtokol"
          entitaId={p.id}
          nazevDokumentu={`Zjišťovací protokol ${p.obdobi}`}
          jmeno={p.smlouva.protistrana}
          onZavrit={() => setProtistrana(false)}
        />
      )}
    </>
  );
}

function TabulkaCerpani({
  nadpis,
  radky,
  editovatelny,
  hodnota,
  onZmena,
}: {
  nadpis: string;
  radky: RadekProtokolu[];
  editovatelny: boolean;
  hodnota: (r: RadekProtokolu) => string;
  onZmena: (r: RadekProtokolu, v: string) => void;
}) {
  return (
    <section className="rounded-xl border border-border bg-surface p-4">
      <h2 className="mb-2 font-bold">{nadpis}</h2>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr>
              <th className={th}>Kód</th>
              <th className={th}>Popis</th>
              <th className={`${th} text-right`}>Celkem</th>
              <th className={`${th} text-right`}>Dříve čerpáno</th>
              <th className={`${th} text-right`}>Čerpání za období</th>
              <th className={`${th} text-right`}>Částka</th>
            </tr>
          </thead>
          <tbody>
            {radky.map((r) => {
              const proc = Number(String(hodnota(r)).replace(',', '.')) || 0;
              const castka = (Number(r.celkem ?? 0) * proc) / 100;
              return (
                <tr key={r.kod + r.popis} className="border-t border-border">
                  <td className={`${td} whitespace-nowrap`}>{r.kod}</td>
                  <td className={td}>{r.popis}</td>
                  <td className={`${td} text-right whitespace-nowrap`}>
                    {r.celkem ? formatKc(r.celkem) : '?'}
                  </td>
                  <td className={`${td} text-right whitespace-nowrap`}>
                    {r.drivezCerpanoProcent} %
                  </td>
                  <td className={`${td} text-right`}>
                    {editovatelny ? (
                      <span className="inline-flex items-center gap-1">
                        <input
                          aria-label={`Čerpání ${r.kod}`}
                          inputMode="decimal"
                          className="w-20 rounded-md border border-border px-2 py-1 text-right text-sm tabular-nums focus:border-primary"
                          value={hodnota(r)}
                          onChange={(e) => onZmena(r, e.target.value)}
                        />
                        %
                      </span>
                    ) : (
                      <span className="tabular-nums">{r.procentoCerpani ?? '—'} %</span>
                    )}
                  </td>
                  <td className={`${td} text-right font-semibold whitespace-nowrap tabular-nums`}>
                    {proc > 0 ? formatKc(String(Math.round(castka))) : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function OdsouhlasitDialog({
  protokolId,
  obdobi,
  jmeno,
  onZavrit,
}: {
  protokolId: string;
  obdobi: string;
  jmeno: string;
  onZavrit: () => void;
}) {
  const queryClient = useQueryClient();
  const [obrazek, setObrazek] = useState<string | null>(null);
  const [chyba, setChyba] = useState<string | null>(null);
  const odsouhlasit = useMutation({
    mutationFn: () =>
      api.post(`/api/protokoly/${protokolId}/odsouhlasit`, {
        podpis: { obrazek, jmeno },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['protokol', protokolId] });
      onZavrit();
    },
    onError: (err) => setChyba(hlaskaZChyby(err)),
  });
  return (
    <Dialog nadpis={`Odsouhlasit protokol ${obdobi}`} onZavrit={onZavrit}>
      <div className="flex flex-col gap-4">
        {chyba && <Chyba>{chyba}</Chyba>}
        <p>
          Odsouhlasením se protokol zamkne a stává se podkladem k faktuře — čerpání už nepůjde
          měnit.
        </p>
        <PodpisVstup
          entita="ZjistovaciProtokol"
          entitaId={protokolId}
          jmeno={jmeno}
          onChange={setObrazek}
        />
        <p className="-mt-2 text-sm text-text-secondary">Podepisuje: {jmeno}</p>
        <div className="flex gap-3">
          <Btn
            size="desktop"
            disabled={odsouhlasit.isPending || !obrazek}
            onClick={() => odsouhlasit.mutate()}
          >
            {odsouhlasit.isPending ? 'Odsouhlasuji…' : 'Odsouhlasit a zamknout'}
          </Btn>
          <Btn size="desktop" varianta="secondary" onClick={onZavrit}>
            Zrušit
          </Btn>
        </div>
      </div>
    </Dialog>
  );
}
